import { useCallback, useEffect, useMemo } from 'react'
import { useLocation, useSearchParams } from 'react-router-dom'
import { useSelector } from 'react-redux'
import dayjs, { Dayjs } from 'dayjs'
import type { DateRange } from '@mui/x-date-pickers-pro'
import { filterScope, filterStorageKey, readEntry, removeEntry, writeEntry } from '@common/filterMemory'

const FORMAT = 'YYYY-MM-DD'
const SCREEN_KEY = 'analyticsDateRange'

/**
 * Date-range filter held in the URL (?from=YYYY-MM-DD&to=YYYY-MM-DD) instead
 * of component state: the range survives report switches and remounts, deep
 * links carry the filter, and the Dashboard email export reads the same
 * params. Drop-in replacement for the useState<DateRange> pattern that pairs
 * with CustomDateRange.
 *
 * The URL does not survive a pathname change or a fresh visit, so the range is
 * also remembered in the dashboards' filter memory (DESIGN_GUIDE.md 7.11),
 * scoped to user, group and building. The URL wins when it carries a range;
 * otherwise the remembered one is written into the URL on mount so every
 * reader (the email export included) sees a single value.
 */
const useDateRangeSearchParams = (
  defaultRange: DateRange<Dayjs> = [null, null]
): [DateRange<Dayjs>, (value: DateRange<Dayjs>) => void] => {
  const [searchParams, setSearchParams] = useSearchParams()
  const { pathname } = useLocation()
  const { user, selectedGroupId, selectedPropertyId } = useSelector((state: any) => state.profile)
  const building = pathname.startsWith('/b/') ? selectedPropertyId : null
  const storageKey = filterStorageKey(filterScope(user?.acId || user?.UserId, selectedGroupId, building), SCREEN_KEY)
  const urlFrom = searchParams.get('from')
  const urlTo = searchParams.get('to')

  // The URL wins; the remembered range stands in when the URL has none. It is
  // resolved here, synchronously, so the first render already carries it and
  // the range's identity does not change when the URL catches up below (a
  // changed identity would fire every "range changed, back to page 0" effect
  // downstream and wipe a remembered page).
  const { from, to } = useMemo(() => {
    if (urlFrom || urlTo) return { from: urlFrom, to: urlTo }
    const stored = readEntry(storageKey)
    const sFrom = typeof stored?.from === 'string' ? stored.from : null
    const sTo = typeof stored?.to === 'string' ? stored.to : null
    return sFrom && sTo ? { from: sFrom, to: sTo } : { from: null, to: null }
  }, [urlFrom, urlTo, storageKey])

  // A range that arrived in the URL (a deep link, a report tab switch) is
  // remembered too, so leaving the screen and coming back keeps it.
  useEffect(() => {
    if (!urlFrom || !urlTo) return
    const start = dayjs(urlFrom, FORMAT)
    const end = dayjs(urlTo, FORMAT)
    if (start.isValid() && end.isValid()) writeEntry(storageKey, { from: urlFrom, to: urlTo })
  }, [urlFrom, urlTo, storageKey])

  // Write the remembered range into the URL so every reader (the email export
  // included) sees a single value; replace: true keeps it out of history.
  useEffect(() => {
    if (urlFrom || urlTo || !from || !to) return
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev)
        next.set('from', from)
        next.set('to', to)
        return next
      },
      { replace: true }
    )
  }, [urlFrom, urlTo, from, to, setSearchParams])

  const range = useMemo<DateRange<Dayjs>>(() => {
    const start = from ? dayjs(from, FORMAT) : null
    const end = to ? dayjs(to, FORMAT) : null
    // The URL stores day precision; re-expand the end to end-of-day to match
    // what CustomDateRange emits (it stamps end 23:59:59 before onChange) —
    // a midnight end would silently drop the final day from fetches.
    if (start?.isValid() && end?.isValid()) {
      return [start, end.endOf('day')]
    }
    return defaultRange
    // defaultRange is deliberately not a dependency: callers pass literals,
    // and re-deriving on identity change would loop the render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [from, to])
  const setRange = useCallback(
    (value: DateRange<Dayjs>) => {
      const valid = !!(value?.[0]?.isValid() && value?.[1]?.isValid())
      if (valid) writeEntry(storageKey, { from: value[0]!.format(FORMAT), to: value[1]!.format(FORMAT) })
      else removeEntry(storageKey)
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev)
          if (valid) {
            next.set('from', value[0]!.format(FORMAT))
            next.set('to', value[1]!.format(FORMAT))
          } else {
            next.delete('from')
            next.delete('to')
          }
          return next
        },
        { replace: true }
      )
    },
    [setSearchParams, storageKey]
  )
  return [range, setRange]
}

export default useDateRangeSearchParams
